/* eslint-disable no-restricted-imports */
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import { getLastAddedChildPartNumber } from 'integration/utils/getLastAddedChildPartNumber';
import { useProduct } from '@/data/Content/Product';
import getValueByPathFromUnknown from 'integration/utils/getValueByPathFromUnknown';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';
import {
	getProductBeforePaymentValue,
	PRODUCT_BEFORE_PAYMENT_ATTRIBUTE,
	PRODUCT_BEFORE_PAYMENT_QUERY_KEY,
} from '@/data/utils/productBeforePayment';
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { addToCartFetcher, BASE_ADD_2_CART_BODY, useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { processError } from '@/data/utils/processError';
import { useInventory } from '@/data/Content/_Inventory';
import { generateOrderItem, tallaOrder } from '@/data/Content/Outfits';
import { SelectChangeEvent } from '@mui/material';
import { useUser } from '@/data/User';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useDismissibleDialog } from '@/utils/dismissibleDialog';

export const FREE_SHIPPING_POPUP_STORAGE_KEY = 'FreeShippingActivatedPopup';

const FREE_SHIPPING_POPUP_HIDDEN_SECONDS = 604800;

export const useFreeShippingDismissible = () => {
	const dismissibleDialog = useDismissibleDialog({
		storageKey: FREE_SHIPPING_POPUP_STORAGE_KEY,
		hiddenSeconds: FREE_SHIPPING_POPUP_HIDDEN_SECONDS,
		skipInitializer: true,
	});

	return dismissibleDialog;
};

export const useFreeShippingActivatedPopup = (
	products: unknown,
	id?: unknown,
	discount?: number,
	onUnavailable?: () => void
) => {
	const dismissibleDialog = useFreeShippingDismissible();

	const [productSelected, setProductSelected] = useState<string | undefined>('');
	const openAuthAddCartFlow = useIsOpen();
	const openAuthRedirectFlow = useIsOpen();
	const router = useNextRouter();
	const localRoutes = useLocalization('Routes');
	const { orderItems } = useCart();
	const { notifyError, clearMessage } = useNotifications();
	const { pickupInStoreShipMode } = useAllowableShippingModes();
	const { settings } = useSettings();
	const { mutateCart } = useCart();
	const params = useExtraRequestParameters();

	const lastAddedPartNumber = useMemo(() => getLastAddedChildPartNumber(orderItems), [orderItems]);
	const { user: { isLoggedIn = false } = {} } = useUser();

	const { product: lastAddedProduct, loading: isLoadingLastAddedProduct } = useProduct({
		id: lastAddedPartNumber,
	});

	const lastAddedProductInfo = useMemo(() => {
		if (!lastAddedProduct) {
			return undefined;
		}

		const gender = findProductAttribute(lastAddedProduct.attributes, 'Genero')?.value;
		return { product: lastAddedProduct, productGender: gender };
	}, [lastAddedProduct]);

	const partNumberProductByGender = useMemo(() => {
		if (!lastAddedProductInfo) {
			return undefined;
		}

		const genderKey = lastAddedProductInfo.productGender?.toLocaleLowerCase() || 'default';

		const freeProductPartNumber = getValueByPathFromUnknown(products, [genderKey]);

		return valueIsString(freeProductPartNumber) ? freeProductPartNumber : undefined;
	}, [lastAddedProductInfo, products]);

	const { product: freeProduct, loading: isLoadingFreeProduct } = useProduct({
		id: partNumberProductByGender,
	});

	const {
		availability: availabilityData,
		loading: isLoadingAvailability,
		error: availabilityError,
	} = useInventory(freeProduct?.items.map((item) => item.partNumber).join(','));

	const availability = useMemo(() => availabilityData ?? [], [availabilityData]);

	const productInfo = useMemo(() => {
		if (!freeProduct) {
			return undefined;
		}

		const category = (() => {
			const family = findProductAttribute(freeProduct.attributes, 'Familia');
			const gender = findProductAttribute(freeProduct.attributes, 'Genero');
			const brand = findProductAttribute(freeProduct.attributes, 'Marca');

			if (brand) {
				return `${brand.value}`;
			}

			if (family && gender) {
				return `${family.value} ${gender.value}`;
			}

			return freeProduct.manufacturer;
		})();

		const prices = (() => {
			const { price, priceWithoutOffer, shouldOffer } = calculateProductPrices({
				list: freeProduct.productPrice.list || 0,
				offer: freeProduct.productPrice.offer || 0,
				ribbons: freeProduct?.ribbons ?? [],
			});

			return {
				price: priceWithoutOffer,
				discount: shouldOffer ? price : undefined,
			};
		})();

		const sizes = freeProduct.items
			.reduce<{ partNumber: string; size: string; id: string }[]>((acc, item) => {
				const availItem = availability.find((avail) => avail.productId === item.id);

				if (!availItem?.status) {
					return acc;
				}

				const size = findProductAttribute(item.attributes, 'Talla')?.value;

				if (size) {
					acc.push({ partNumber: item.partNumber, size, id: item.id });
				}
				return acc;
			}, [])
			.sort((a, b) => {
				const sizeALower = a.size.toLowerCase();
				const sizeBLower = b.size.toLowerCase();

				if (tallaOrder[sizeALower] && tallaOrder[sizeBLower]) {
					return tallaOrder[sizeALower] - tallaOrder[sizeBLower];
				}

				if (tallaOrder[sizeALower]) return -1;
				if (tallaOrder[sizeBLower]) return 1;

				const numA = Number(a.size);
				const numB = Number(b.size);

				if (!isNaN(numA) && !isNaN(numB)) {
					return numA - numB;
				}

				return a.size.localeCompare(b.size);
			});

		return {
			product: freeProduct,
			productCategory: category,
			productPrices: prices,
			productSizes: sizes,
		};
	}, [freeProduct, availability]);

	const giftUnavailable = useMemo(() => {
		if (isLoadingLastAddedProduct || isLoadingFreeProduct) {
			return false;
		}

		if (!partNumberProductByGender || !freeProduct) {
			return true;
		}

		if (isLoadingAvailability || (!availabilityData && !availabilityError)) {
			return false;
		}

		return (productInfo?.productSizes.length ?? 0) === 0;
	}, [
		isLoadingLastAddedProduct,
		isLoadingFreeProduct,
		partNumberProductByGender,
		freeProduct,
		isLoadingAvailability,
		availabilityData,
		availabilityError,
		productInfo,
	]);

	const unavailableNotified = useRef(false);

	useEffect(() => {
		if (!onUnavailable || !giftUnavailable || unavailableNotified.current) {
			return;
		}

		unavailableNotified.current = true;
		onUnavailable();
	}, [giftUnavailable, onUnavailable]);

	const secondItemDiscount = useMemo(() => {
		const recommendedPrice = productInfo?.productPrices.price ?? 0;

		if (!productInfo || !discount || discount <= 0 || recommendedPrice <= 0) {
			return { applies: false, discountedPrice: undefined as number | undefined };
		}

		const { totalUnits, cheaperUnits } = (orderItems ?? []).reduce(
			(acc, item) => {
				const qty = Number(item.quantity) || 0;
				const unitPrice = Number(item.unitPrice);

				acc.totalUnits += qty;

				if (!isNaN(unitPrice) && unitPrice < recommendedPrice) {
					acc.cheaperUnits += qty;
				}

				return acc;
			},
			{ totalUnits: 0, cheaperUnits: 0 }
		);

		const discountsGranted = Math.floor((totalUnits + 1) / 2);

		if (cheaperUnits >= discountsGranted) {
			return { applies: false, discountedPrice: undefined as number | undefined };
		}

		const discountedPrice = Math.round(recommendedPrice * (1 - discount / 100) * 100) / 100;

		return { applies: true, discountedPrice };
	}, [productInfo, orderItems, discount]);

	const productInfoWithDiscount = useMemo(() => {
		if (
			!productInfo ||
			!secondItemDiscount.applies ||
			secondItemDiscount.discountedPrice === undefined
		) {
			return productInfo;
		}

		return {
			...productInfo,
			productPrices: {
				price: productInfo.productPrices.price,
				discount: secondItemDiscount.discountedPrice,
			},
		};
	}, [productInfo, secondItemDiscount]);

	const hasUniqueSize = useMemo(() => {
		const sizes = productInfo?.productSizes ?? [];
		return sizes.length === 1 && sizes[0].size.trim().toLowerCase() === 'u';
	}, [productInfo?.productSizes]);
	const hasAutoOpenedRef = useRef(false);

	const productBeforePaymentId = useMemo(() => getProductBeforePaymentValue(id), [id]);

	/**
	 * Href al PDP del producto de regalo. Lleva el id del espot como query param para que el add to
	 * cart del PDP tambien pueda marcar el order item con product_before_payment.
	 */
	const productHref = useMemo(() => {
		const seoHref = freeProduct?.seo?.href;

		if (!seoHref) {
			return undefined;
		}

		return {
			pathname: seoHref,
			query: { [PRODUCT_BEFORE_PAYMENT_QUERY_KEY]: productBeforePaymentId },
		};
	}, [freeProduct?.seo?.href, productBeforePaymentId]);

	const onClosePopup = useCallback(() => {
		dismissibleDialog.closeSkippedDialog();

		if (!isLoggedIn) {
			openAuthRedirectFlow.on();
			return;
		}

		router.push({ pathname: localRoutes.CheckOut.route.t() });
	}, [localRoutes.CheckOut.route, router, dismissibleDialog, isLoggedIn, openAuthRedirectFlow]);

	const onViewProduct = useCallback(() => {
		dismissibleDialog.closeDirectDialog();
	}, [dismissibleDialog]);

	const onAuthFlowClose = useCallback(() => {
		dismissibleDialog.closeDirectDialog();
		openAuthAddCartFlow.off();
		openAuthRedirectFlow.off();
	}, [openAuthAddCartFlow, openAuthRedirectFlow, dismissibleDialog]);

	const onAddToCartCommon = useCallback(async () => {
		const childProduct = productInfo?.productSizes.find((ip) => ip.partNumber === productSelected);

		if (!childProduct || !productInfo) {
			notifyError('No se han seleccionado la talla');
			return;
		}

		const orderItemExtendAttribute = [
			{
				attributeName: PRODUCT_BEFORE_PAYMENT_ATTRIBUTE,
				attributeValue: productBeforePaymentId,
			},
		];

		try {
			await addToCartFetcher(true)(
				settings?.storeId ?? '',
				{},
				{
					...BASE_ADD_2_CART_BODY,
					orderItem: [
						{
							...generateOrderItem({
								childrenProductId: childProduct.id,
								childrenPartNumber: childProduct.partNumber,
								quantity: 1,
								availability,
								storeName: settings.storeName,
								pickupInStoreShipMode,
							}),
							orderItemExtendAttribute,
						},
					],
				},
				params
			);

			await mutateCart();

			dismissibleDialog.closeSkippedDialog();
			onAuthFlowClose();
			router.push({ pathname: localRoutes.CheckOut.route.t() });
			scrollTo(0, 0);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		}
	}, [
		productBeforePaymentId,
		productSelected,
		notifyError,
		availability,
		settings,
		pickupInStoreShipMode,
		params,
		productInfo,
		mutateCart,
		localRoutes.CheckOut.route,
		router,
		dismissibleDialog,
		onAuthFlowClose,
	]);

	const onAddToCart = useCallback(
		async (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			if (!isLoggedIn) {
				openAuthAddCartFlow.on();
				dismissibleDialog.closeSkippedDialog();
				return;
			}

			await onAddToCartCommon();
		},
		[isLoggedIn, openAuthAddCartFlow, onAddToCartCommon, dismissibleDialog]
	);

	const onSelectProductSize = useCallback((event: SelectChangeEvent<string>) => {
		setProductSelected(event.target.value);
	}, []);

	useEffect(() => {
		if (
			!hasAutoOpenedRef.current &&
			productInfo?.productSizes &&
			productInfo?.productSizes.length > 0
		) {
			hasAutoOpenedRef.current = true;
			clearMessage();
			dismissibleDialog.openDialog();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productInfo?.productSizes, dismissibleDialog.openDialog]);

	useEffect(() => {
		const sizes = productInfo?.productSizes || [];

		if ((sizes.length > 1 || hasUniqueSize) && !productSelected) {
			setProductSelected(sizes[0].partNumber);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productInfo?.productSizes, productSelected, hasUniqueSize]);

	return {
		productInfo: productInfoWithDiscount,
		appliesSecondItemDiscount: secondItemDiscount.applies,
		productSelected,
		productHref,
		hideSizeSelect: hasUniqueSize,
		isLoadingProducts: isLoadingLastAddedProduct || isLoadingFreeProduct,
		isLoadingAvailability,
		openPopup: dismissibleDialog.open,
		openAuthAddCartFlow: openAuthAddCartFlow.open,
		openAuthRedirectFlow: openAuthRedirectFlow.open,
		onClosePopup,
		onAddToCart,
		onSelectProductSize,
		onViewProduct,
		onAuthFlowClose,
		onAuthSuccess: onAddToCartCommon,
	};
};
